import {createReducer, on} from '@ngrx/store';
import {DevicePendingActionState} from '@birco/rainmesh-api';
import {initialState} from './device.state';
import {DeviceActions} from './device.actions';
import {DeviceResponseDto} from '../models/device-response.interface';

const mergePendingFields = (
  currentDevice: DeviceResponseDto | undefined,
  incomingDevice: DeviceResponseDto,
): DeviceResponseDto => {
  const hasPendingValue = Object.prototype.hasOwnProperty.call(incomingDevice, 'pendingValue');
  const hasPendingState = Object.prototype.hasOwnProperty.call(incomingDevice, 'pendingState');
  const hasPendingAttemptCount = Object.prototype.hasOwnProperty.call(incomingDevice, 'pendingAttemptCount');

  return {
    ...currentDevice,
    ...incomingDevice,
    pendingValue: hasPendingValue ? (incomingDevice.pendingValue ?? undefined) : currentDevice?.pendingValue,
    pendingState: hasPendingState ? (incomingDevice.pendingState ?? undefined) : currentDevice?.pendingState,
    pendingAttemptCount: hasPendingAttemptCount ? (incomingDevice.pendingAttemptCount ?? undefined) : currentDevice?.pendingAttemptCount,
  };
};

export const deviceReducer = createReducer(
  initialState,

  on(DeviceActions.public.updateDeviceFilter, (state, {filter}) => ({
    ...state,
    filter: filter
  })),

  on(DeviceActions.private.loadDevicesWithFilters, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),

  on(DeviceActions.private.loadDevicesSuccess, (state, {devicesResponseDto}) => {
    const entities = {...state.entities};
    devicesResponseDto.devices.forEach((it) => {
      entities[it.id!!] = mergePendingFields(entities[it.id!!], it);
    });

    return {
      ...state,
      entities: entities,
      pages: {
        ...state.pages,
        [state.filter!!.page!!]: devicesResponseDto.devices.map((it) => it.id!!),
      },
      totalCount: devicesResponseDto.totalCount,
      isLoading: false,
      error: null,
    };
  }),

  on(DeviceActions.private.loadDevicesFailure, (state, {error}) => ({
    ...state,
    isLoading: false,
    error: error,
  })),

  on(DeviceActions.private.loadDevicesWithProvidedFilters, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),

  on(DeviceActions.private.loadDevicesWithProvidedFiltersSuccess, (state, props) => {
    const entities = {...state.entities};
    props.devicesResponseDto.devices.forEach((it) => {
      entities[it.id!!] = mergePendingFields(entities[it.id!!], it);
    });

    return {
      ...state,
      entities: entities,
      isLoading: false,
      error: null,
    };
  }),

  on(DeviceActions.private.loadDevicesWithProvidedFiltersFailure, (state, error) => ({
    ...state,
    isLoading: false,
    error: error,
  })),

  on(DeviceActions.public.loadAllDevicesByFilters, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),

  on(DeviceActions.private.loadAllDevicesByFiltersSuccess, (state, props) => {
    return {
      ...state,
      isLoading: false,
      error: null,
    };
  }),

  on(DeviceActions.private.loadAllDevicesByFiltersFailure, (state, error) => ({
    ...state,
    isLoading: false,
    error: error,
  })),

  on(DeviceActions.private.loadNextOfAll, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),

  on(DeviceActions.private.loadNextOfAllSuccess, (state, props) => {
    const entities = {...state.entities};
    props.devicesResponseDto.devices.forEach((it) => {
      entities[it.id!!] = mergePendingFields(entities[it.id!!], it);
    });

    return {
      ...state,
      entities: entities,
    };
  }),

  on(DeviceActions.private.loadNextOfAllFailure, (state, error) => ({
    ...state,
    isLoading: false,
    error: error,
  })),

  on(DeviceActions.public.triggerDeviceAction, (state, props) => {
    const entities = {...state.entities};
    const currentDevice = entities[props.id];

    if (currentDevice) {
      entities[props.id] = {
        ...currentDevice,
        pendingValue: props.value,
        pendingState: DevicePendingActionState.QUEUED,
        pendingAttemptCount: currentDevice.pendingAttemptCount ?? 0,
      };
    }

    return {
      ...state,
      entities,
      isLoading: true,
      error: null,
    };
  }),

  on(DeviceActions.private.triggerDeviceActionSuccess, (state, props) => {
    const entities = {...state.entities};
    entities[props.responseDto.id] = mergePendingFields(entities[props.responseDto.id], props.responseDto);

    return {
      ...state,
      entities,
      isLoading: false,
      error: null,
    };
  }),

  on(DeviceActions.private.triggerDeviceActionFailure, (state, props) => {
    const entities = {...state.entities};
    const currentDevice = entities[props.id];

    if (currentDevice?.pendingValue === props.attemptedValue) {
      entities[props.id] = {
        ...currentDevice,
        pendingValue: undefined,
        pendingState: undefined,
        pendingAttemptCount: undefined,
      };
    }

    return {
      ...state,
      entities,
      isLoading: false,
      error: props.error,
    };
  }),

  on(DeviceActions.public.updateDevice, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),

  on(DeviceActions.private.updateDeviceSuccess, (state, {responseDto}) => {
    const entities = {...state.entities};
    entities[responseDto.id] = mergePendingFields(entities[responseDto.id], responseDto);

    return {
      ...state,
      entities,
      isLoading: false,
      error: null,
    };
  }),

  on(DeviceActions.private.updateDeviceFailure, (state, {error}) => ({
    ...state,
    isLoading: false,
    error: error,
  })),

  on(DeviceActions.public.loadDeviceById, (state, props) => ({
    ...state,
    isLoading: true,
    error: null,
  })),

  on(DeviceActions.private.loadDeviceByIdSuccess, (state, props) => {
    const entities = {...state.entities};
    entities[props.responseDto.id] = mergePendingFields(entities[props.responseDto.id], props.responseDto);

    return {
      ...state,
      entities,
      isLoading: false,
      error: null,
    };
  }),

  on(DeviceActions.private.loadDeviceByIdFailure, (state, props) => ({
    ...state,
    isLoading: false,
    error: props.error,
  })),

  on(DeviceActions.public.refreshDeviceDataFromWebSocket, (state, props) => {
    const entities = {...state.entities};
    const currentDevice = entities[props.device.id];
    const hasPendingValue = Object.prototype.hasOwnProperty.call(props.device, 'pendingValue');
    const hasPendingState = Object.prototype.hasOwnProperty.call(props.device, 'pendingState');
    const hasPendingAttemptCount = Object.prototype.hasOwnProperty.call(props.device, 'pendingAttemptCount');

    entities[props.device.id] = {
      ...currentDevice,
      id: props.device.id,
      value: props.device.value,
      pendingValue: hasPendingValue ? (props.device.pendingValue ?? undefined) : currentDevice?.pendingValue,
      pendingState: hasPendingState ? (props.device.pendingState ?? undefined) : currentDevice?.pendingState,
      pendingAttemptCount: hasPendingAttemptCount ? (props.device.pendingAttemptCount ?? undefined) : currentDevice?.pendingAttemptCount,
      lastSeenDate: props.device.lastSeenDate
    };

    return {
      ...state,
      entities,
    }
  }),
);
